import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindSupplierMappingQueryResponse,
  FindSupplierMappingQueryParams,
} from "../types/FindSupplierMapping"

type FindSupplierMappingClient = typeof client<
  FindSupplierMappingQueryResponse,
  Error,
  never
>
type FindSupplierMapping = {
  data: FindSupplierMappingQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: FindSupplierMappingQueryParams
  headerParams: never
  response: Awaited<ReturnType<FindSupplierMappingClient>>
  client: {
    parameters: Partial<Parameters<FindSupplierMappingClient>[0]>
    return: Awaited<ReturnType<FindSupplierMappingClient>>
  }
}
export const findSupplierMappingQueryKey = (
  params: FindSupplierMapping["queryParams"]
) =>
  [
    { url: "/v1/client-settings/find-supplier-mapping" },
    ...(params ? [params] : []),
  ] as const
export type FindSupplierMappingQueryKey = ReturnType<
  typeof findSupplierMappingQueryKey
>
export function findSupplierMappingQueryOptions<
  TData = FindSupplierMapping["response"],
  TQueryData = FindSupplierMapping["response"],
>(
  params: FindSupplierMapping["queryParams"],
  options: FindSupplierMapping["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindSupplierMapping["response"],
    FindSupplierMapping["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findSupplierMappingQueryKey(params)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindSupplierMapping["data"],
        FindSupplierMapping["error"]
      >({
        method: "get",
        url: `/v1/client-settings/find-supplier-mapping`,
        params,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find supplier mapping
 * @link /v1/client-settings/find-supplier-mapping
 */
export function useFindSupplierMapping<
  TData = FindSupplierMapping["response"],
  TQueryData = FindSupplierMapping["response"],
  TQueryKey extends QueryKey = FindSupplierMappingQueryKey,
>(
  params: FindSupplierMapping["queryParams"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindSupplierMapping["response"],
        FindSupplierMapping["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindSupplierMapping["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindSupplierMapping["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? findSupplierMappingQueryKey(params)
  const query = useQuery<
    FindSupplierMapping["data"],
    FindSupplierMapping["error"],
    TData,
    any
  >({
    ...findSupplierMappingQueryOptions<TData, TQueryData>(
      params,
      clientOptions
    ),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindSupplierMapping["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
